// Phase compiler-edit-submission-metadata (TODO-062) — in-place
// edit modal for the 5 most-commonly-fixed submission metadata fields
// (drug name, indication, applicant, sequence description, submission
// type). Mounted from CompilerPage so publishers don't have to navigate
// away from the Compiler to correct a typo or pick a different type.
//
// Backend contract: standard `PATCH /api/v1/submissions/:id` — the
// existing endpoint accepts all five fields; Indication/Applicant are
// now typed on the V2 Submission interface (phase-submission-type-fixes).

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  v2,
  ApiError,
  type CompanyProfile,
  type Submission,
  type SubmissionTypeGroup,
} from '@/api/v2Client';
import { MovableDialog } from '@/components/shared/MovableDialog';

export interface EditSubmissionModalProps {
  readonly submission: Submission;
  /** Called when the modal should close (cancel OR successful save). */
  readonly onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  readonly onSaved?: () => void;
}

interface DraftState {
  drugName: string;
  indication: string;
  applicant: string;
  sequenceDescription: string;
  submissionType: string;
  /** phase-missing-profile-visibility (item 4) — relink path. '' = unlinked. */
  companyProfileId: string;
}

function initialDraft(sub: Submission): DraftState {
  return {
    drugName: sub.DrugName ?? '',
    indication: sub.Indication ?? '',
    applicant: sub.Applicant ?? '',
    sequenceDescription: sub.SequenceDescription ?? '',
    submissionType: sub.SubmissionType ?? '',
    companyProfileId: sub.CompanyProfileId ?? '',
  };
}

export function EditSubmissionModal({
  submission,
  onClose,
  onSaved,
}: EditSubmissionModalProps): ReactElement {
  const [draft, setDraft] = useState<DraftState>(() => initialDraft(submission));
  const [types, setTypes] = useState<readonly SubmissionTypeGroup[] | null>(null);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // phase-missing-profile-visibility (item 4) — company profile picker.
  const [profiles, setProfiles] = useState<readonly CompanyProfile[] | null>(null);
  const [profilesError, setProfilesError] = useState<string | null>(null);

  // Re-prime draft if a new submission row arrives while the modal is
  // mounted (e.g. concurrent refetch).
  useEffect(() => {
    setDraft(initialDraft(submission));
  }, [submission]);

  // Load valid submission types for the current region. Failures are
  // surfaced inline but don't block the save — the operator may pick
  // any value or leave the dropdown disabled.
  useEffect(() => {
    let cancelled = false;
    v2.submissionTypes
      .list({ region: submission.Region })
      .then((res) => {
        if (cancelled) return;
        // apiFetch already unwraps { ok, data } — read the payload directly.
        setTypes(res.submissionTypes);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setTypesError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load submission types',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [submission.Region]);

  // phase-missing-profile-visibility (item 4) — active company profiles for
  // the relink picker. Not region-scoped (a profile isn't tied to a market).
  useEffect(() => {
    let cancelled = false;
    v2.companyProfiles
      .list()
      .then((res) => {
        if (cancelled) return;
        setProfiles(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setProfilesError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load company profiles',
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = useMemo(() => {
    const orig = initialDraft(submission);
    return (
      orig.drugName !== draft.drugName ||
      orig.indication !== draft.indication ||
      orig.applicant !== draft.applicant ||
      orig.sequenceDescription !== draft.sequenceDescription ||
      orig.submissionType !== draft.submissionType ||
      orig.companyProfileId !== draft.companyProfileId
    );
  }, [draft, submission]);

  const drugNameInvalid = draft.drugName.trim() === '';
  const seqDescTooLong = draft.sequenceDescription.length > 255;
  const canSubmit = !saving && isDirty && !drugNameInvalid && !seqDescTooLong;

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (!canSubmit) return;
      setSaving(true);
      setSaveError(null);
      try {
        const orig = initialDraft(submission);
        // Only send changed fields — keeps audit diffs tight and avoids
        // touching server-managed timestamps unnecessarily.
        const patch: Partial<Submission> = {};
        if (draft.drugName !== orig.drugName) patch.DrugName = draft.drugName.trim();
        if (draft.indication !== orig.indication)
          patch.Indication = draft.indication.trim() || null;
        if (draft.applicant !== orig.applicant)
          patch.Applicant = draft.applicant.trim() || null;
        if (draft.sequenceDescription !== orig.sequenceDescription)
          patch.SequenceDescription = draft.sequenceDescription.trim() || null;
        if (draft.submissionType !== orig.submissionType)
          patch.SubmissionType = draft.submissionType || null;
        if (draft.companyProfileId !== orig.companyProfileId)
          patch.CompanyProfileId = draft.companyProfileId || null;
        await v2.submissions.update(submission.Id, patch);
        if (onSaved) onSaved();
        onClose();
      } catch (err: unknown) {
        setSaveError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Save failed',
        );
      } finally {
        setSaving(false);
      }
    },
    [canSubmit, draft, submission, onClose, onSaved],
  );

  const setField = <K extends keyof DraftState>(key: K, value: string): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  // phase-movable-dialogs — shared movable shell; overlay click still closes.
  return (
    <MovableDialog
      persistKey="compiler.editSubmission"
      title={<span id="edit-submission-modal-title">Edit submission metadata</span>}
      ariaLabelledBy="edit-submission-modal-title"
      testId="edit-submission-modal"
      onClose={onClose}
      closeOnBackdrop
      width="min(560px, 92vw)"
      maxHeight="90vh"
      zIndex={1000}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >

        {/* Drug name (required) */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            Drug name <span style={{ color: 'var(--danger, #b91c1c)' }}>*</span>
          </span>
          <input
            data-testid="edit-submission-drug-name"
            type="text"
            value={draft.drugName}
            onChange={(e) => setField('drugName', e.target.value)}
            required
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
            }}
          />
        </label>

        {/* Indication */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Indication</span>
          <input
            data-testid="edit-submission-indication"
            type="text"
            value={draft.indication}
            onChange={(e) => setField('indication', e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
            }}
          />
        </label>

        {/* Applicant */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Applicant name</span>
          <input
            data-testid="edit-submission-applicant"
            type="text"
            value={draft.applicant}
            onChange={(e) => setField('applicant', e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
            }}
          />
        </label>

        {/* Sequence description */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            Sequence description{' '}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
              ({draft.sequenceDescription.length}/255)
            </span>
          </span>
          <input
            data-testid="edit-submission-sequence-description"
            type="text"
            value={draft.sequenceDescription}
            onChange={(e) => setField('sequenceDescription', e.target.value)}
            maxLength={255}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
            }}
          />
          {seqDescTooLong && (
            <span
              role="alert"
              style={{ color: 'var(--danger, #b91c1c)', fontSize: 11 }}
            >
              Sequence description must be 255 characters or fewer.
            </span>
          )}
        </label>

        {/* Submission type */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Submission type</span>
          <select
            data-testid="edit-submission-type"
            value={draft.submissionType}
            onChange={(e) => setField('submissionType', e.target.value)}
            disabled={types === null && !typesError}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
              background: '#fff',
            }}
          >
            <option value="">— select a type —</option>
            {/* If the existing value isn't in the catalog (legacy / freeform),
                keep it visible as a degenerate option so the operator's
                current selection isn't silently dropped. */}
            {draft.submissionType &&
              !(types ?? []).some((g) => g.code === draft.submissionType) && (
                <option value={draft.submissionType}>
                  {draft.submissionType} (legacy)
                </option>
              )}
            {(types ?? []).map((g) => (
              <option key={g.code} value={g.code}>
                {g.name}
              </option>
            ))}
          </select>
          {typesError && (
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              Could not load type catalog ({typesError}). You can still pick
              from any existing value or save without changing the type.
            </span>
          )}
        </label>

        {/* phase-missing-profile-visibility (item 4) — the relink path the
            SubmissionHeroCard's missing/never-linked badge opens. */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Company profile</span>
          <select
            data-testid="edit-submission-company-profile"
            value={draft.companyProfileId}
            onChange={(e) => setField('companyProfileId', e.target.value)}
            disabled={profiles === null && !profilesError}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 14,
              background: '#fff',
            }}
          >
            <option value="">— none linked (required: an explicit link before generation) —</option>
            {/* Degenerate option so a currently-linked-but-deleted profile
                doesn't silently collapse the select to blank on open. */}
            {draft.companyProfileId &&
              !(profiles ?? []).some((p) => p.Id === draft.companyProfileId) && (
                <option value={draft.companyProfileId}>
                  {`(missing/deleted profile ${draft.companyProfileId})`}
                </option>
              )}
            {(profiles ?? []).map((p) => (
              <option key={p.Id} value={p.Id}>
                {p.CompanyName}
              </option>
            ))}
          </select>
          {profilesError && (
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              Could not load company profiles ({profilesError}).
            </span>
          )}
        </label>

        {saveError && (
          <div
            role="alert"
            data-testid="edit-submission-error"
            style={{
              background: 'var(--danger-bg, rgba(185,28,28,0.10))',
              color: 'var(--danger, #b91c1c)',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {saveError}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 8,
          }}
        >
          <button
            type="button"
            data-testid="edit-submission-cancel"
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="edit-submission-save"
            disabled={!canSubmit}
            style={{
              background: canSubmit ? 'var(--primary)' : 'var(--text-muted)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </MovableDialog>
  );
}
